import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * App-wide toast notifications. One stack, top-right, above everything. Any
 * component calls `useToast().push(text, tone)` to raise one; it auto-dismisses
 * after `durationMs` (pass `null` to keep it until dismissed — used for sticky
 * errors like the Clerk session-sync failure). `push` returns the toast id so a
 * caller can dismiss it itself (e.g. when the underlying condition clears).
 */
export type ToastTone = "error" | "info" | "success";

type Toast = {
  id: number;
  text: string;
  tone: ToastTone;
};

type PushOptions = { durationMs?: number | null; onDismiss?: () => void };

type ToastApi = {
  push: (text: string, tone?: ToastTone, opts?: PushOptions) => number;
  /** Remove a toast programmatically (e.g. its condition cleared). Does NOT
   *  fire the toast's `onDismiss` — that's reserved for an explicit user close. */
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<
  ToastTone,
  { icon: typeof Info; accent: string; text: string }
> = {
  error: {
    icon: AlertTriangle,
    accent: "var(--color-mx-magenta)",
    text: "text-white",
  },
  info: {
    icon: Info,
    accent: "var(--color-mx-electric)",
    text: "text-white",
  },
  success: {
    icon: CheckCircle2,
    accent: "#3ecf8e",
    text: "text-white",
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  // onDismiss handlers live outside state so removal never runs a side-effect
  // inside the state updater (which React can double-invoke in StrictMode).
  const handlers = useRef(new Map<number, () => void>());

  const remove = useCallback((id: number) => {
    handlers.current.delete(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  // Explicit user close (the X): remove AND fire the toast's onDismiss.
  const userDismiss = useCallback((id: number) => {
    const onDismiss = handlers.current.get(id);
    handlers.current.delete(id);
    setToasts((list) => list.filter((t) => t.id !== id));
    onDismiss?.();
  }, []);

  const push = useCallback(
    (text: string, tone: ToastTone = "info", opts?: PushOptions) => {
      seq.current += 1;
      const id = seq.current;
      if (opts?.onDismiss) handlers.current.set(id, opts.onDismiss);
      setToasts((list) => [...list, { id, text, tone }]);
      const duration = opts?.durationMs === undefined ? 5000 : opts.durationMs;
      if (duration !== null) {
        window.setTimeout(() => remove(id), duration);
      }
      return id;
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({ push, dismiss: remove }),
    [push, remove],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toaster toasts={toasts} onDismiss={userDismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
}

function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[100] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2.5 pointer-events-none"
      data-testid="toaster"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const tone = TONE[t.tone];
        const Icon = tone.icon;
        return (
          <div
            key={t.id}
            role="status"
            data-testid={`toast-${t.tone}`}
            className={`pointer-events-auto flex items-start gap-3 rounded-lg border border-[var(--color-mx-line-strong)] bg-[var(--color-mx-black)]/95 pl-3.5 pr-3 py-3 shadow-xl shadow-black/60 backdrop-blur-md [animation:mx-toast-in_180ms_cubic-bezier(0.16,1,0.3,1)] ${tone.text}`}
            style={{ borderLeft: `3px solid ${tone.accent}` }}
          >
            <Icon
              size={18}
              strokeWidth={2}
              className="mt-px shrink-0"
              style={{ color: tone.accent }}
              aria-hidden
            />
            <span className="flex-1 text-[13px] leading-snug">{t.text}</span>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              className="-mt-0.5 -mr-1 shrink-0 rounded p-1 text-white/40 transition-colors hover:text-white"
              aria-label="Dismiss"
            >
              <X size={15} strokeWidth={1.8} aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
